import { useState } from 'react'
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldGroup,
  Fieldset,
  Input,
  Label,
  MediumText,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  StrongText,
  Subheading,
  SurfaceCard,
  SurfaceRow,
  surfaceRowClassName,
} from '../../../components/ui'
import { ClipboardDocumentListIcon, PlusIcon } from '@heroicons/react/20/solid'
import { uiClasses } from '../../shared/uiClasses'
import type { FormEvent } from 'react'
import type { Medication, MedicationDraft } from '../types'

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

const INITIAL_MEDICATION_FORM: MedicationDraft = {
  dosage: '',
  frequency: 'Once daily',
  name: '',
  timeOfDay: 'Morning',
}

interface MedicationsTabProps {
  medications: Medication[]
  onAddMedication: (draft: MedicationDraft) => void
}

export default function MedicationsTab({ medications, onAddMedication }: MedicationsTabProps) {
  const [medForm, setMedForm] = useState<MedicationDraft>(INITIAL_MEDICATION_FORM)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  const active = medications.filter((medication) => medication.status === 'active')
  const discontinued = medications.filter((medication) => medication.status === 'discontinued')
  const normalizedName = medForm.name.trim()
  const normalizedDosage = medForm.dosage.trim()
  const isSubmitDisabled = !normalizedName || !normalizedDosage

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatusMessage(null)

    const nextDraft = {
      ...medForm,
      dosage: normalizedDosage,
      name: normalizedName,
    }

    if (!nextDraft.name || !nextDraft.dosage) {
      setStatusMessage('Medication name and dosage are required.')
      return
    }

    onAddMedication(nextDraft)
    setMedForm(INITIAL_MEDICATION_FORM)
    setStatusMessage(null)
  }

  return (
    <div className="space-y-6">
      {/* Add medication */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Add a medication</Subheading>
        <form onSubmit={handleSubmit} className="mt-5">
          <Fieldset>
            <FieldGroup className="!space-y-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field>
                  <Label>Medication name</Label>
                  <Input
                    value={medForm.name}
                    onChange={(event) => setMedForm((form) => ({ ...form, name: event.target.value }))}
                    placeholder="e.g. Metoprolol"
                  />
                </Field>
                <Field>
                  <Label>Dosage</Label>
                  <Input
                    value={medForm.dosage}
                    onChange={(event) => setMedForm((form) => ({ ...form, dosage: event.target.value }))}
                    placeholder="e.g. 25 mg"
                  />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field>
                  <Label>Frequency</Label>
                  <Select
                    value={medForm.frequency}
                    onValueChange={(nextValue) => setMedForm((form) => ({ ...form, frequency: nextValue }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Once daily">Once daily</SelectItem>
                      <SelectItem value="Twice daily">Twice daily</SelectItem>
                      <SelectItem value="Three times daily">Three times daily</SelectItem>
                      <SelectItem value="As needed">As needed</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <Label>Time of day</Label>
                  <Select
                    value={medForm.timeOfDay}
                    onValueChange={(nextValue) => setMedForm((form) => ({ ...form, timeOfDay: nextValue }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Morning">Morning</SelectItem>
                      <SelectItem value="Evening">Evening</SelectItem>
                      <SelectItem value="Morning & evening">Morning & evening</SelectItem>
                      <SelectItem value="With meals">With meals</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button type="submit" disabled={isSubmitDisabled}>
                  <PlusIcon data-slot="icon" />
                  Add medication
                </Button>
                {statusMessage && (
                  <StatusText aria-live="polite">
                    {statusMessage}
                  </StatusText>
                )}
              </div>
            </FieldGroup>
          </Fieldset>
        </form>
      </SurfaceCard>

      {/* Active medications */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Active medications</Subheading>
        <div className="mt-5 space-y-3.5">
          {active.length > 0 ? (
            active.map((medication) => (
              <SurfaceRow
                key={medication.id}
                className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <StrongText>{medication.name}</StrongText>
                      <Badge variant="zinc">Active</Badge>
                    </div>
                    <p className={`text-sm/6 ${TEXT_SECONDARY}`}>
                      {medication.dosage} · {medication.frequency} · {medication.timeOfDay}
                    </p>
                    <p className={`text-xs/5 ${TEXT_FAINT}`}>
                      Prescribed by {medication.prescriber} · Since {medication.startDate}
                    </p>
                  </div>
                  {medication.notes && (
                    <p className={`mt-2 text-xs/5 italic ${TEXT_SUBTLE}`}>{medication.notes}</p>
                  )}
                </div>
                <div className="sm:text-right">
                  <p className={`text-xs/5 ${TEXT_FAINT}`}>Next refill</p>
                  <MediumText tone="secondary">{medication.refillDate}</MediumText>
                </div>
              </SurfaceRow>
            ))
          ) : (
            <EmptyState
              className={`${surfaceRowClassName} py-7`}
              icon={ClipboardDocumentListIcon}
              title="No medications"
              description="Add your first medication above to start tracking."
              titleClassName={TEXT_SECONDARY}
              descriptionClassName={TEXT_SUBTLE}
              iconClassName={TEXT_FAINT}
            />
          )}
        </div>
      </SurfaceCard>

      {/* Discontinued */}
      {discontinued.length > 0 && (
        <SurfaceCard as="article">
          <Subheading variant="dashboard">Discontinued</Subheading>
          <div className="mt-5 space-y-3.5">
            {discontinued.map((medication) => (
              <SurfaceRow key={medication.id} className="opacity-60">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <MediumText className="line-through" tone="secondary">{medication.name}</MediumText>
                    <Badge variant="zinc">Discontinued</Badge>
                  </div>
                  <p className={`text-sm/6 ${TEXT_SUBTLE}`}>{medication.dosage} · {medication.frequency}</p>
                </div>
                {medication.notes && (
                  <p className={`mt-2 text-xs/5 italic ${TEXT_SUBTLE}`}>{medication.notes}</p>
                )}
              </SurfaceRow>
            ))}
          </div>
        </SurfaceCard>
      )}
    </div>
  )
}
